import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  EmailTemplateDto,
  CreateEmailTemplateDto,
  UpdateEmailTemplateDto,
  EmailTemplateFilterDto,
  PaginatedEmailTemplateResponse,
  TemplateTypeOption,
  ApiResponse
} from '@/types/email-template.types';

/**
 * Email Template Service
 * Handles all API calls related to email template management
 */
class EmailTemplateService {
  /**
   * Get all email templates with optional filtering and pagination
   * @param filters - Filter criteria (template_type, is_active, search, include_deleted, page, limit)
   * @returns Paginated list of email templates
   */
  async getAllTemplates(filters?: EmailTemplateFilterDto): Promise<PaginatedEmailTemplateResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.template_type) {
        queryParams.append('template_type', filters.template_type);
      }
      if (filters?.is_active !== undefined) {
        queryParams.append('is_active', String(filters.is_active));
      }
      if (filters?.include_deleted) {
        queryParams.append('include_deleted', String(filters.include_deleted));
      }
      if (filters?.search) {
        queryParams.append('search', filters.search);
      }
      if (filters?.page !== undefined) {
        queryParams.append('page', String(filters.page));
      }
      if (filters?.limit !== undefined) {
        queryParams.append('limit', String(filters.limit));
      }

      const url = `${API_ENDPOINTS.EMAIL_TEMPLATES.LIST}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<ApiResponse<PaginatedEmailTemplateResponse>>(url);

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch email templates');
    } catch (error: any) {
      console.error('Error fetching email templates:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch email templates';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get all available email template types
   * @returns Array of template type options with value and label
   */
  async getTemplateTypes(): Promise<TemplateTypeOption[]> {
    try {
      const response = await apiClient.get<ApiResponse<TemplateTypeOption[]>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.GET_TYPES
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch template types');
    } catch (error: any) {
      console.error('Error fetching template types:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch template types';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get email template by ID
   * @param id - Template MongoDB ObjectId
   * @returns Email template details
   */
  async getTemplateById(id: string): Promise<EmailTemplateDto> {
    try {
      const response = await apiClient.get<ApiResponse<EmailTemplateDto>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.GET_BY_ID(id)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch email template');
    } catch (error: any) {
      console.error('Error fetching email template:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch email template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get email template by key
   * @param key - Template unique key
   * @returns Email template details
   */
  async getTemplateByKey(key: string): Promise<EmailTemplateDto> {
    try {
      const response = await apiClient.get<ApiResponse<EmailTemplateDto>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.GET_BY_KEY(key)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch email template');
    } catch (error: any) {
      console.error('Error fetching email template by key:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch email template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get templates by type
   * @param type - Email template type
   * @returns List of templates of specified type
   */
  async getTemplatesByType(type: string): Promise<EmailTemplateDto[]> {
    try {
      const response = await apiClient.get<ApiResponse<EmailTemplateDto[]>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.GET_BY_TYPE(type)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch templates by type');
    } catch (error: any) {
      console.error('Error fetching templates by type:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch templates by type';
      throw new Error(errorMessage);
    }
  }

  /**
   * Create a new email template
   * @param templateData - Template creation data
   * @returns Created template
   */
  async createTemplate(templateData: CreateEmailTemplateDto): Promise<EmailTemplateDto> {
    try {
      const response = await apiClient.post<ApiResponse<EmailTemplateDto>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.CREATE,
        templateData
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create email template');
    } catch (error: any) {
      console.error('Error creating email template:', error);

      // Handle specific error codes
      if (error?.response?.data?.error?.code === 'DUPLICATE_TEMPLATE_KEY') {
        throw new Error('Template key already exists. Please use a different key.');
      }
      if (error?.response?.data?.error?.code === 'UNDEFINED_VARIABLE') {
        throw new Error(error.response.data.error.message || 'Template uses undefined variables.');
      }

      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to create email template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing email template
   * @param id - Template ID
   * @param templateData - Template update data (partial)
   * @returns Updated template
   */
  async updateTemplate(id: string, templateData: UpdateEmailTemplateDto): Promise<EmailTemplateDto> {
    try {
      const response = await apiClient.put<ApiResponse<EmailTemplateDto>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.UPDATE(id),
        templateData
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update email template');
    } catch (error: any) {
      console.error('Error updating email template:', error);

      // Handle specific error codes
      if (error?.response?.data?.error?.code === 'UNDEFINED_VARIABLE') {
        throw new Error(error.response.data.error.message || 'Template uses undefined variables.');
      }

      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to update email template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete an email template (soft delete)
   * @param id - Template ID
   * @returns Deletion confirmation
   */
  async deleteTemplate(id: string): Promise<{ success: boolean; code: number }> {
    try {
      const response = await apiClient.delete<ApiResponse<{ success: boolean; code: number }>>(
        API_ENDPOINTS.EMAIL_TEMPLATES.DELETE(id)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete email template');
    } catch (error: any) {
      console.error('Error deleting email template:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to delete email template';
      throw new Error(errorMessage);
    }
  }

}

export const emailTemplateService = new EmailTemplateService();
export default emailTemplateService;
